import { Box, Flex, Heading, Spinner, Text } from '@chakra-ui/react';
import { FunctionComponent, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { setSelectedClient, useAppDispatch } from '@/redux';
import { useApplicationsQuery } from '@/queries';
import { useAuth } from '@/auth';
import { BusinessLineGroup, ClientSummary, GroupEntry } from '@/types/applications';
import BusinessLineSection from '@/pages/home/components/BusinessLineSection';

const groupByBusinessLine = (groups: GroupEntry[]): BusinessLineGroup[] => {
  const byBusinessLine = new Map<string, ClientSummary[]>();

  groups.forEach((group) => {
    const clients = byBusinessLine.get(group.businessLine) ?? [];
    clients.push({ groupId: group.groupId, groupName: group.groupName });
    byBusinessLine.set(group.businessLine, clients);
  });

  return Array.from(byBusinessLine.entries()).map(([businessLine, clients]) => ({
    businessLine,
    clients,
  }));
};

export const Home: FunctionComponent = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data, isLoading, isError, error } = useApplicationsQuery(user?.email);
  const dispatch = useAppDispatch();

  const businessLineGroups = useMemo(() => groupByBusinessLine(data?.groups ?? []), [data]);

  const handleClientSelect = (client: ClientSummary) => {
    const fullGroup = data?.groups.find((g) => g.groupId === client.groupId) ?? null;
    dispatch(setSelectedClient(fullGroup));

    navigate('/wave-upload');
  };

  return (
    <Box height="100%" overflowY="auto" bg="marketcast.base.background" p={{ base: 6, md: 10 }}>
      <Box maxW="1400px" mx="auto">
        <Box mb={8}>
          <Heading size="lg" color="marketcast.text.primary" mb={2}>
            Wave Over Wave Dashboard
          </Heading>
          <Text fontSize="md" color="marketcast.gray.2">
            Select a client to get started with data processing.
          </Text>
        </Box>

        {isLoading && (
          <Flex direction="column" align="center" justify="center" gap={4} py="60px">
            <Spinner size="lg" color="marketcast.primaryCta.1" thickness="3px" />
            <Text color="marketcast.text.primary" fontWeight="500">
              Loading your applications...
            </Text>
          </Flex>
        )}

        {isError && (
          <Flex direction="column" align="center" justify="center" gap={2} py="60px">
            <Text color="red.500" fontWeight="600">
              Unable to load your applications.
            </Text>
            {error instanceof Error && (
              <Text color="marketcast.gray.2" fontSize="sm">
                {error.message}
              </Text>
            )}
          </Flex>
        )}

        {!isLoading && !isError && businessLineGroups.length === 0 && (
          <Flex direction="column" align="center" justify="center" gap={2} py="60px">
            <Text color="marketcast.text.primary" fontWeight="500">
              No applications found for your account.
            </Text>
          </Flex>
        )}

        {!isLoading &&
          !isError &&
          businessLineGroups.map((group) => (
            <BusinessLineSection
              key={group.businessLine}
              group={group}
              onClientSelect={handleClientSelect}
            />
          ))}
      </Box>
    </Box>
  );
};
